import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Shield, Eye, EyeOff } from 'lucide-react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <Shield className="w-8 h-8 text-[#FF3B30]" />
            <span className="font-heading text-white font-bold text-2xl tracking-tight">RiskCoach<span className="text-[#FF3B30]">.ai</span></span>
          </Link>
          <h1 className="font-heading text-2xl font-bold text-white">Welcome Back</h1>
          <p className="text-[#A3A3A3] text-sm mt-2">Sign in to continue your coaching journey</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-[#141414] border border-[#262626] rounded-sm p-6 space-y-4">
          {error && (
            <div data-testid="login-error" className="bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-[#FF3B30] text-sm rounded-sm p-3">
              {error}
            </div>
          )}

          <div>
            <label className="text-[#A3A3A3] text-xs tracking-[0.2em] uppercase font-bold block mb-2">Email</label>
            <input
              data-testid="login-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full bg-[#0A0A0A] border border-[#262626] rounded-sm px-4 py-3 text-white placeholder:text-[#737373] focus:outline-none focus:ring-1 focus:ring-[#FF3B30] focus:border-[#FF3B30] transition-all"
              placeholder="you@company.com"
            />
          </div>

          <div>
            <label className="text-[#A3A3A3] text-xs tracking-[0.2em] uppercase font-bold block mb-2">Password</label>
            <div className="relative">
              <input
                data-testid="login-password-input"
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full bg-[#0A0A0A] border border-[#262626] rounded-sm px-4 py-3 text-white placeholder:text-[#737373] focus:outline-none focus:ring-1 focus:ring-[#FF3B30] focus:border-[#FF3B30] transition-all pr-10"
                placeholder="Enter your password"
              />
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#737373] hover:text-white transition-colors"
              >
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <button
            data-testid="login-submit-button"
            type="submit"
            disabled={loading}
            className="w-full bg-[#FF3B30] hover:bg-[#D62828] text-white font-bold py-3 rounded-sm transition-colors disabled:opacity-50"
          >
            {loading ? 'Signing In...' : 'Sign In'}
          </button>

          <div className="flex items-center justify-between text-sm">
            <Link to="/forgot-password" data-testid="forgot-password-link" className="text-[#737373] hover:text-white transition-colors">
              Forgot password?
            </Link>
            <span className="text-[#A3A3A3]">
              No account?{' '}
              <Link to="/register" data-testid="goto-register-link" className="text-[#FF3B30] hover:underline font-medium">
                Start Program
              </Link>
            </span>
          </div>
        </form>
      </div>
    </div>
  );
}
